import { ReactElement } from 'react';
import { TextField, SelectField, CheckField, FormGroup, useField } from '@ff-it/form';
import { vatStrategyOptions } from 'configuration';
import { Fieldset } from 'components';
import { required } from 'utilities';
import { CompanyField } from 'modules/core/company/components';
import { HorizonConfigFields } from 'modules/horizon/ConfigFields';
import { OneCConfigFields } from './OneCIntegration/ConfigFields';
import { Option } from 'options';
import type { AccountingIntegrationKey } from 'modules/core/types';
import type { InvoiceLanguage } from 'modules/invoicing/types';

const avbRowTitleDefaults: Record<InvoiceLanguage, [label: string, title: string]> = {
  LV: ['Latvian', 'Plānošanas komisija'],
  LT: ['Lithuanian', 'Planavimo komisinis mokestis'],
  ET: ['Estonian', 'Planeerimistasu'],
  EN: ['English', 'Planning commission'],
};

const accountingIntegrationOptions: Option<AccountingIntegrationKey>[] = [
  { label: '1C', value: 'onec' },
  { label: 'Horizon', value: 'horizon' },
];

function AccountingIntegrationSection(): ReactElement {
  const {
    input: { value: key },
  } = useField<AccountingIntegrationKey | null>('accounting_integration_key', { subscription: { value: true } });
  return (
    <>
      <Fieldset title="Accounting">
        <div className="row">
          <SelectField
            name="vat_strategy"
            label="VAT strategy"
            className="col-md-6"
            options={vatStrategyOptions}
            required
            validate={required}
            simple
          />
          <SelectField
            name="accounting_integration_key"
            label="Accounting integration"
            className="col-md-6"
            options={accountingIntegrationOptions}
            simple
            isClearable
          />
        </div>
      </Fieldset>
      {key === 'onec' && <OneCConfigFields />}
      {key === 'horizon' && <HorizonConfigFields />}
    </>
  );
}

function BaseFields(): ReactElement | null {
  const {
    input: { value: external },
  } = useField('external', { subscription: { value: true } });
  if (external) {
    return null;
  }
  return (
    <>
      <AccountingIntegrationSection />
      <Fieldset title="Invoices">
        <CheckField
          name="income_default_hide_issuer"
          label="Hide issuer by default"
          variant="checkbox"
          help="Initial value for new income invoices"
        />
        <FormGroup label="AVB row titles" help="The service period is appended automatically.">
          <div className="row">
            {Object.entries(avbRowTitleDefaults).map(([language, [label, title]]) => (
              <TextField
                key={language}
                name={`avb_default_row_title.${language}`}
                label={
                  <span className="mr-2 d-inline-block" style={{ width: '6rem' }}>
                    {label}
                  </span>
                }
                placeholder={title}
                className="col-md-6 mb-2 d-flex align-items-baseline"
                size="sm"
              />
            ))}
          </div>
        </FormGroup>
      </Fieldset>
      <Fieldset title="Agreements">
        <CheckField
          name="direct_agreements"
          label="Direct agreements"
          variant="checkbox"
          help="Only directly sold products are available"
        />
      </Fieldset>
      <Fieldset title="Plan export">
        <CheckField
          name="plan_naming_sheet"
          label="Naming sheet"
          variant="checkbox"
          help="Include generated row names in RTB plans"
        />
      </Fieldset>
    </>
  );
}

export function Form(): ReactElement {
  return (
    <div className="container px-0 ml-0">
      <div className="row">
        <CompanyField name="company" label="Company" className="col-md-6" required includeBranches={false} />
        <TextField name="slug" label="Slug" className="col-md-3" required />
        <CheckField
          name="external"
          className="col-md-3"
          label="External agency"
          variant="switch"
          help="Not a member of system"
        />
      </div>
      <BaseFields />
    </div>
  );
}
